import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import stageConfig from "../stage-config";
import AlertNotification from "../components/AlertNotification";
import AppBackService from "../services/appback";
import { RootContext } from "../context/root-provider";
import s from "./ApiKeys.module.scss";

// Mirrors domain/serv/api_keys.py SUPPORTED_SCOPES minus the reserved admin
// pair (TT-782 decision lock). Server validates strictly; keep in sync.
const SCOPE_OPTIONS = [
  "read:docs",
  "write:docs",
  "read:taps",
  "write:taps",
  "read:meetings",
  "write:meetings",
  "read:resources",
  "write:resources",
  "read:notifs",
  "write:notifs",
];
const DEFAULT_SCOPES = ["read:docs", "write:docs"];
const MAX_ACTIVE_KEYS = 3;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

const keyDisplayStatus = (key) => {
  if (key.status !== "active") return "Revoked";
  if (key.expires_at && new Date(key.expires_at) < new Date()) return "Expired";
  return "Active";
};

const ApiKeys = () => {
  const { userInfo } = useContext(RootContext);
  const userId = userInfo?.username;

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);

  // create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState(DEFAULT_SCOPES);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // one-time reveal modal (create + rotate share it)
  const [revealKey, setRevealKey] = useState(null);
  const [copied, setCopied] = useState(false);

  // confirm modals
  const [rotateTarget, setRotateTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  // toast
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState(null);
  const toast = useCallback((message, severity = null) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  }, []);

  const activeCount = useMemo(() => keys.filter((k) => keyDisplayStatus(k) === "Active").length, [keys]);

  const loadKeys = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const res = await AppBackService.listApiKeys(userId);
    if (res && !res.error) {
      setKeys(res.keys || []);
    } else {
      toast("Could not load API keys. Please retry.", "error");
    }
    setLoading(false);
  }, [userId, toast]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const apiErrorMessage = (res, fallback) => {
    if (res?.status === 409) return "Active key limit reached. Revoke one key to create another.";
    return res?.data?.detail || res?.msg || fallback;
  };

  const handleCreate = useCallback(async () => {
    if (!newName.trim()) {
      setFormError("Name is required.");
      return;
    }
    if (!newScopes.length) {
      setFormError("Select at least one scope.");
      return;
    }
    setSubmitting(true);
    setFormError("");
    const res = await AppBackService.createApiKey(userId, { name: newName.trim(), scopes: newScopes });
    setSubmitting(false);
    if (res && !res.error) {
      setCreateOpen(false);
      setNewName("");
      setNewScopes(DEFAULT_SCOPES);
      setRevealKey(res.apiKey);
      loadKeys();
    } else if (res?.status === 409) {
      setCreateOpen(false);
      toast(apiErrorMessage(res), "warning");
    } else if (res?.status === 400) {
      setFormError(apiErrorMessage(res, "Invalid input."));
    } else {
      setFormError("Something went wrong. Please retry.");
    }
  }, [userId, newName, newScopes, loadKeys, toast]);

  const handleRotate = useCallback(async () => {
    const target = rotateTarget;
    setRotateTarget(null);
    const res = await AppBackService.rotateApiKey(userId, target.id);
    if (res && !res.error) {
      setRevealKey(res.apiKey);
      loadKeys();
    } else {
      toast(apiErrorMessage(res, "Could not rotate key."), "error");
    }
  }, [userId, rotateTarget, loadKeys, toast]);

  const handleRevoke = useCallback(async () => {
    const target = revokeTarget;
    setRevokeTarget(null);
    // optimistic update; reconcile with a reload either way
    setKeys((prev) => prev.map((k) => (k.id === target.id ? { ...k, status: "revoked" } : k)));
    const res = await AppBackService.revokeApiKey(userId, target.id);
    if (res?.error) {
      toast(apiErrorMessage(res, "Could not revoke key."), "error");
    }
    loadKeys();
  }, [userId, revokeTarget, loadKeys, toast]);

  const handleCopy = useCallback(async () => {
    // Clipboard only — never echo the key into logs/telemetry.
    try {
      await navigator.clipboard.writeText(revealKey);
      setCopied(true);
    } catch (e) {
      toast("Copy failed — select the key text and copy manually.", "warning");
    }
  }, [revealKey, toast]);

  const closeReveal = useCallback(() => {
    setRevealKey(null);
    setCopied(false);
  }, []);

  const toggleScope = useCallback((scope) => {
    setNewScopes((prev) => (prev.includes(scope) ? prev.filter((x) => x !== scope) : [...prev, scope]));
  }, []);

  return (
    <div className={s.pageContainer}>
      <div className={s.pageContent}>
        <div className={s.pageTitle}>API Keys</div>

        <div className={s.section}>
          <div className={s.sectionHeader}>
            <div className={s.sectionTitle}>Your Keys</div>
            <Button
              variant="contained"
              size="small"
              id="api_key_create_button"
              disabled={activeCount >= MAX_ACTIVE_KEYS}
              onClick={() => setCreateOpen(true)}>
              Create Key
            </Button>
          </div>
          {activeCount >= MAX_ACTIVE_KEYS && (
            <div className={s.limitNote}>
              You have {MAX_ACTIVE_KEYS} active keys (the maximum). Revoke one to create another.
            </div>
          )}

          {loading ? (
            <div className={s.emptyState}>Loading…</div>
          ) : keys.length === 0 ? (
            <div className={s.emptyState}>No API keys yet. Create one to use the external API.</div>
          ) : (
            <div className={s.tableWrap}>
              <table className={s.keysTable}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Scopes</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Last used</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => {
                    const status = keyDisplayStatus(key);
                    return (
                      <tr key={key.id}>
                        <td>{key.name}</td>
                        <td className={s.mono}>{key.key_prefix}…</td>
                        <td className={s.scopesCell}>{(key.scopes || []).join(", ")}</td>
                        <td>
                          <span className={`${s.badge} ${s[`badge${status}`]}`}>{status}</span>
                        </td>
                        <td>{fmtDate(key.created_at)}</td>
                        <td>{fmtDate(key.expires_at)}</td>
                        <td>{fmtDate(key.last_used_at)}</td>
                        <td className={s.rowActions}>
                          {status === "Active" && (
                            <>
                              <Button size="small" onClick={() => setRotateTarget(key)}>
                                Rotate
                              </Button>
                              <Button size="small" color="error" onClick={() => setRevokeTarget(key)}>
                                Revoke
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className={s.section}>
          <div className={s.sectionTitle}>Use your key</div>
          <div className={s.quickstartText}>
            Send requests to the external API with your key in a header. Preferred:
          </div>
          <pre className={s.codeBlock}>{`X-API-Key: <your-key>`}</pre>
          <div className={s.quickstartText}>Also supported:</div>
          <pre className={s.codeBlock}>{`Authorization: ApiKey <your-key>`}</pre>
          <div className={s.quickstartText}>Create a document from a YouTube video:</div>
          <pre className={s.codeBlock}>{`curl -X POST ${stageConfig.appbackUrl}/api/v1/docs \\
  -H "X-API-Key: <your-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "youtube", "url": "https://www.youtube.com/watch?v=..."}'`}</pre>
          <div className={s.quickstartText}>Create a document from text:</div>
          <pre className={s.codeBlock}>{`curl -X POST ${stageConfig.appbackUrl}/api/v1/docs \\
  -H "X-API-Key: <your-key>" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "text", "title": "My notes", "text": "..."}'`}</pre>
          <div className={s.quickstartText}>
            File uploads (.docx, .rtf, .txt) send <code>file_name</code> + base64 <code>file_data</code> in the same
            body. Requires the <code>write:docs</code> scope.
          </div>
        </div>
      </div>

      {/* Create key modal */}
      <Dialog open={createOpen} onClose={() => !submitting && setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          {formError && <div className={s.errorMsg}>{formError}</div>}
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            id="api_key_name"
            placeholder="Key name (e.g. my-integration)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <div className={s.scopesTitle}>Scopes</div>
          <div className={s.scopesGrid}>
            {SCOPE_OPTIONS.map((scope) => (
              <FormControlLabel
                key={scope}
                control={<Checkbox size="small" checked={newScopes.includes(scope)} onChange={() => toggleScope(scope)} />}
                label={<span className={s.mono}>{scope}</span>}
              />
            ))}
          </div>
          <div className={s.expiryNote}>Key expires 90 days after creation.</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" id="api_key_create_submit" onClick={handleCreate} disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* One-time reveal modal */}
      <Dialog open={Boolean(revealKey)} onClose={closeReveal} fullWidth maxWidth="sm">
        <DialogTitle>Your new API key</DialogTitle>
        <DialogContent>
          <div className={s.revealWarning}>This key is shown once; store it now.</div>
          <pre className={`${s.codeBlock} ${s.revealKey}`}>{revealKey}</pre>
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopyIcon />} onClick={handleCopy}>
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button variant="contained" onClick={closeReveal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rotate confirm */}
      <Dialog open={Boolean(rotateTarget)} onClose={() => setRotateTarget(null)}>
        <DialogTitle>Rotate key "{rotateTarget?.name}"?</DialogTitle>
        <DialogContent>
          The current key stops working immediately and a new key is issued. Integrations using the old key must be
          updated.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotateTarget(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRotate}>
            Rotate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke confirm */}
      <Dialog open={Boolean(revokeTarget)} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke key "{revokeTarget?.name}"?</DialogTitle>
        <DialogContent>This cannot be undone. Anything using this key stops working immediately.</DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRevoke}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <AlertNotification message={alertMessage} severity={alertSeverity} open={alertOpen} setOpen={setAlertOpen} />
    </div>
  );
};

export default ApiKeys;
